<template>
  <div class="chat-messages" ref="messageContainer">
    <div v-if="messages.length === 0" class="chat-empty">
      <div class="ui icon header">
        <i class="comments icon"></i>
        <div class="content">
          你好，我是 AI 助手
          <div class="sub header">基于知识库内容回答你的问题，试试向我提问吧</div>
        </div>
      </div>
    </div>
    <div v-for="(msg, index) in messages" :key="index" class="chat-message" :class="msg.role">
      <div class="message-avatar">
        <div v-if="msg.role === 'user'" class="avatar user-avatar">
          <i class="user icon"></i>
        </div>
        <div v-else class="avatar ai-avatar">
          <span class="ai-avatar-text">AI</span>
        </div>
      </div>
      <div class="message-content">
        <div class="message-role">{{ msg.role === 'user' ? '你' : 'AI 助手' }}</div>
        <div
          v-if="msg.role === 'user'"
          class="message-text user-text"
        >{{ msg.content }}</div>
        <div
          v-else
          class="message-text ai-text"
          :class="{ 'streaming': isLastAssistantMessage(index) && loading }"
          ref="aiMessages"
        ></div>
        <div class="message-time">{{ msg.time }}</div>
      </div>
    </div>
    <div v-if="loading && !isStreaming" class="chat-message assistant">
      <div class="message-avatar">
        <div class="avatar ai-avatar">
          <span class="ai-avatar-text">AI</span>
        </div>
      </div>
      <div class="message-content">
        <div class="message-role">AI 助手</div>
        <div class="message-text typing-indicator">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import sanitizeHtml from 'sanitize-html'

marked.setOptions({
	gfm: true,
	breaks: true
})

const renderer = new marked.Renderer()
renderer.code = function (code, infostring) {
	const language = infostring || 'text'
	const langLabel = language.toUpperCase()
	const escapedCode = code
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
	return `<pre class="code-block language-${language}"><div class="code-header"><span class="code-lang">${langLabel}</span></div><code class="language-${language}">${escapedCode}</code></pre>`
}
renderer.codespan = function (text) {
	return `<code class="inline-code">${text}</code>`
}
renderer.link = function (href, title, text) {
	return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
}
renderer.image = function (href, title, text) {
	return `<img src="${href}" alt="${text || ''}" style="max-width:100%;">`
}
renderer.table = function (header, body) {
	return `<div style="overflow-x: auto; margin: 10px 0;"><table class="ui celled table"><thead>${header}</thead><tbody>${body}</tbody></table></div>`
}

marked.use({ renderer })

const SANITIZE_OPTIONS = {
	allowedTags: [
		'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
		'p', 'br', 'hr',
		'strong', 'em', 'b', 'i', 'del', 's',
		'a', 'img',
		'pre', 'code',
		'ul', 'ol', 'li',
		'blockquote',
		'table', 'thead', 'tbody', 'tr', 'th', 'td',
		'input',
		'span', 'div'
	],
	allowedAttributes: {
	'a': ['href', 'target', 'rel'],
	'img': ['src', 'alt', 'style'],
	'code': ['class'],
	'pre': ['class'],
	'span': ['class', 'style'],
	'div': ['class', 'style'],
	'table': ['class'],
	'td': ['style', 'align'],
	'th': ['style', 'align'],
	'input': ['type', 'disabled', 'checked']
},
	transformTags: {
		'a': sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' })
	},
	disallowedTagsMode: 'escape'
}

export default {
	name: 'ChatMessages',
	props: {
		messages: {
			type: Array,
			default: () => []
		},
		loading: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			renderPending: false
		}
	},
	computed: {
		isStreaming() {
			if (this.messages.length === 0) return false
			const lastMessage = this.messages[this.messages.length - 1]
			return lastMessage.role === 'assistant' && this.loading
		}
	},
	watch: {
		messages: {
			handler() {
				this.scheduleRender()
			},
			deep: true
		},
		loading(newVal) {
			if (!newVal) {
				this.$nextTick(() => {
					this.renderAiMessages()
					this.highlightAllCode()
				})
			}
			this.scrollToBottom()
		}
	},
	methods: {
		isLastAssistantMessage(index) {
			return index === this.messages.length - 1 && this.messages[index].role === 'assistant'
		},

		scheduleRender() {
			if (this.renderPending) return
			this.renderPending = true
			requestAnimationFrame(() => {
				this.renderPending = false
				this.renderAiMessages()
			})
		},

		renderAiMessages() {
			const aiRefs = this.$refs.aiMessages
			if (!aiRefs) return

			const aiMessageIndices = []
			this.messages.forEach((msg, i) => {
				if (msg.role === 'assistant') aiMessageIndices.push(i)
			})

			const refArray = Array.isArray(aiRefs) ? aiRefs : [aiRefs]

			aiMessageIndices.forEach((msgIndex, refIndex) => {
				if (refIndex >= refArray.length) return
				const el = refArray[refIndex]
				if (!el) return

				const msg = this.messages[msgIndex]
				if (!msg || msg.content === undefined) return

				const isStreaming = this.isLastAssistantMessage(msgIndex) && this.loading
				const html = this.renderMarkdown(msg.content, isStreaming)

				if (el.innerHTML !== html) {
					el.innerHTML = html
					if (!isStreaming) {
						this.highlightCodeInElement(el)
					}
				}
			})

			this.scrollToBottom()
		},

		preprocessMarkdownTables(text) {
			const lines = text.split('\n')
			let result = []
			let tableBuffer = []
			let inTable = false
			let inCodeBlock = false

			for (let i = 0; i < lines.length; i++) {
				const line = lines[i]

				if (line.trim().startsWith('```')) {
					inCodeBlock = !inCodeBlock
					if (inTable && tableBuffer.length >= 2) {
						const fixedTable = this.fixMarkdownTable(tableBuffer)
						result.push(...fixedTable)
					} else if (inTable) {
						result.push(...tableBuffer)
					}
					tableBuffer = []
					inTable = false
					result.push(line)
					continue
				}

				if (inCodeBlock) {
					result.push(line)
					continue
				}

				const pipeCount = (line.match(/\|/g) || []).length

				if (pipeCount >= 2) {
					tableBuffer.push(line)
					inTable = true
				} else {
					if (inTable && tableBuffer.length >= 2) {
						const fixedTable = this.fixMarkdownTable(tableBuffer)
						result.push(...fixedTable)
					} else if (inTable) {
						result.push(...tableBuffer)
					}
					result.push(line)
					tableBuffer = []
					inTable = false
				}
			}

			if (inTable && tableBuffer.length >= 2) {
				const fixedTable = this.fixMarkdownTable(tableBuffer)
				result.push(...fixedTable)
			} else if (inTable) {
				result.push(...tableBuffer)
			}

			return result.join('\n')
		},

		fixMarkdownTable(lines) {
			const isSeparatorCell = (cell) => {
				return cell.match(/^[:-]+$/) && !cell.match(/[a-zA-Z\u4e00-\u9fff]/)
			}

			const allCells = lines.map(line => {
				return line
					.split('|')
					.map(s => s.trim())
					.filter(s => s.length > 0)
			}).filter(row => row.length > 0)

			if (allCells.length < 2) {
				return lines
			}

			let isStandardTable = false
			if (allCells.length >= 2) {
				const secondRow = allCells[1]
				const allAreSeparator = secondRow.every(cell => isSeparatorCell(cell))
				if (allAreSeparator) {
					isStandardTable = true
				}
			}

			if (isStandardTable) {
				return allCells.map(row => {
					return '| ' + row.join(' | ') + ' |'
				})
			}

			// 清理每行：只保留非分隔符的单元格作为数据
			const cleanedRows = allCells.map(row => {
				return row.filter(cell => !isSeparatorCell(cell))
			}).filter(row => row.length > 0)

			if (cleanedRows.length < 2) {
				return lines
			}

			const colCountFreq = {}
			cleanedRows.forEach(row => {
				colCountFreq[row.length] = (colCountFreq[row.length] || 0) + 1
			})

			let mostCommonColCount = 0
			let maxFreq = 0
			Object.entries(colCountFreq).forEach(([count, freq]) => {
				if (freq > maxFreq && Number(count) >= 2) {
					maxFreq = freq
					mostCommonColCount = Number(count)
				}
			})

			if (mostCommonColCount < 2) {
				return lines
			}

			const normalizedRows = cleanedRows.map(row => {
				const result = []
				for (let i = 0; i < mostCommonColCount; i++) {
					result.push(row[i] || '')
				}
				return result
			})

			const finalRows = []
			finalRows.push('| ' + normalizedRows[0].join(' | ') + ' |')

			const separatorCells = []
			for (let i = 0; i < mostCommonColCount; i++) {
				separatorCells.push(':---')
			}
			finalRows.push('| ' + separatorCells.join(' | ') + ' |')

			for (let i = 1; i < normalizedRows.length; i++) {
				finalRows.push('| ' + normalizedRows[i].join(' | ') + ' |')
			}

			return finalRows
		},

		renderMarkdown(content, isStreaming) {
			if (!content) return isStreaming ? '<span class="typing-cursor"></span>' : ''

			let text = content
			if (isStreaming) {
				const openCount = (text.match(/```/g) || []).length
				if (openCount % 2 !== 0) {
					text += '\n```'
				}
			}

			text = this.preprocessMarkdownTables(text)
			text = this.preprocessMarkdownLists(text)

			let html = marked.parse(text)

			html = sanitizeHtml(html, SANITIZE_OPTIONS)

			if (isStreaming) {
				html += '<span class="typing-cursor"></span>'
			}

			return html
		},

		preprocessMarkdownLists(text) {
			const lines = text.split('\n')
			const result = []
			const listPattern = /^(\s*)([-*+]|\d+\.)\s/
			let inCodeBlock = false

			for (let i = 0; i < lines.length; i++) {
				const line = lines[i]

				if (line.trim().startsWith('```')) {
					inCodeBlock = !inCodeBlock
				}

				if (inCodeBlock) {
					result.push(line)
					continue
				}

				const isListItem = listPattern.test(line)
				const prevLine = result.length > 0 ? result[result.length - 1] : ''
				const prevIsBlank = prevLine.trim() === ''
				const prevIsListItem = listPattern.test(prevLine)

				if (isListItem && !prevIsBlank && !prevIsListItem) {
					result.push('')
				}

				result.push(line)
			}

			return result.join('\n')
		},

		highlightCodeInElement(el) {
			if (typeof Prism === 'undefined') return
			const codeBlocks = el.querySelectorAll('pre code[class*="language-"]')
			codeBlocks.forEach(block => {
				if (!block.dataset.highlighted) {
					Prism.highlightElement(block)
					block.dataset.highlighted = 'true'
				}
			})
		},

		highlightAllCode() {
			if (typeof Prism === 'undefined') return
			this.$nextTick(() => {
				const aiRefs = this.$refs.aiMessages
				if (!aiRefs) return
				const refArray = Array.isArray(aiRefs) ? aiRefs : [aiRefs]
				refArray.forEach(el => {
					if (el) this.highlightCodeInElement(el)
				})
			})
		},

		scrollToBottom() {
			this.$nextTick(() => {
				const container = this.$refs.messageContainer
				if (container) {
					container.scrollTop = container.scrollHeight
				}
			})
		}
	}
}
</script>

<style scoped>
.chat-messages {
	flex: 1;
	overflow-y: auto;
	padding: 20px 40px;
	scroll-behavior: smooth;
}

.chat-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	text-align: center;
	color: #999;
}

.chat-empty .comments.icon {
	font-size: 48px;
	color: #48dbfb;
	margin-bottom: 10px;
}

.chat-message {
	display: flex;
	margin-bottom: 20px;
	animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
	from { opacity: 0; transform: translateY(10px); }
	to { opacity: 1; transform: translateY(0); }
}

.chat-message.user {
	flex-direction: row-reverse;
}

.message-avatar {
	flex-shrink: 0;
}

.avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
}

.user-avatar {
	background: #48dbfb;
	color: white;
}

.ai-avatar {
	background: #6c5ce7;
	color: white;
}

.ai-avatar-text {
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 1px;
}

.message-content {
	max-width: 70%;
	margin: 0 12px;
	position: relative;
}

.chat-message.user .message-content {
	text-align: right;
}

.message-role {
	font-size: 12px;
	color: #999;
	margin-bottom: 4px;
}

.message-text {
	display: inline-block;
	padding: 12px 16px;
	border-radius: 12px;
	line-height: 1.6;
	font-size: 15px;
	word-break: break-word;
	text-align: left;
}

.user-text {
	background: #48dbfb;
	color: white;
	border-bottom-right-radius: 4px;
	white-space: pre-wrap;
}

.ai-text {
	background: white;
	color: #2c3e50;
	border: 1px solid #e8e8e8;
	border-bottom-left-radius: 4px;
	font-size: 15px;
	line-height: 1.8;
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
}

.ai-text.streaming {
	min-height: 20px;
}

/* ===== 代码块 ===== */
.ai-text :deep(pre.code-block) {
	background: #1e1e2e;
	border-radius: 8px;
	margin: 12px 0;
	overflow: hidden;
	border: 1px solid #313244;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.ai-text :deep(pre.code-block .code-header) {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 14px;
	background: #181825;
	border-bottom: 1px solid #313244;
}

.ai-text :deep(pre.code-block .code-lang) {
	font-size: 12px;
	color: #89b4fa;
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
	text-transform: uppercase;
	letter-spacing: 0.8px;
	font-weight: 500;
}

.ai-text :deep(pre.code-block .code-header::after) {
	content: '';
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: #45475a;
}

.ai-text :deep(pre.code-block code) {
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
	font-size: 14px;
	line-height: 1.7;
	background: transparent;
	padding: 16px 18px;
	display: block;
	overflow-x: auto;
	-moz-tab-size: 4;
	-o-tab-size: 4;
	tab-size: 4;
	white-space: pre;
	color: #cdd6f4;
}

/* ===== 内联代码 ===== */
.ai-text :deep(code.inline-code) {
	background: #eef1f6;
	padding: 2px 7px;
	border-radius: 4px;
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
	font-size: 0.9em;
	color: #c7254e;
	border: 1px solid #e1e4ea;
}

.ai-text :deep(pre.code-block code.inline-code) {
	background: transparent;
	padding: 16px 18px;
	color: #cdd6f4;
	border-radius: 0;
	font-size: 14px;
	border: none;
}

/* ===== 标题 ===== */
.ai-text :deep(h1),
.ai-text :deep(h2),
.ai-text :deep(h3),
.ai-text :deep(h4),
.ai-text :deep(h5),
.ai-text :deep(h6) {
	margin: 18px 0 10px 0;
	font-weight: 700;
	line-height: 1.4;
	color: #1a1a2e;
}

.ai-text :deep(h1) { font-size: 1.4em; border-bottom: 1px solid #eef0f4; padding-bottom: 6px; }
.ai-text :deep(h2) { font-size: 1.25em; border-bottom: 1px solid #eef0f4; padding-bottom: 5px; }
.ai-text :deep(h3) { font-size: 1.15em; }
.ai-text :deep(h4) { font-size: 1.05em; }
.ai-text :deep(h5) { font-size: 1em; }
.ai-text :deep(h6) { font-size: 0.95em; color: #666; }

.ai-text :deep(h1:first-child),
.ai-text :deep(h2:first-child),
.ai-text :deep(h3:first-child) {
	margin-top: 4px;
}

/* ===== 段落 ===== */
.ai-text :deep(p) {
	margin: 8px 0;
}

.ai-text :deep(p:first-child) {
	margin-top: 0;
}

.ai-text :deep(p:last-child) {
	margin-bottom: 0;
}

/* ===== 列表 ===== */
.ai-text :deep(ul),
.ai-text :deep(ol) {
	margin: 8px 0;
	padding-left: 24px;
}

.ai-text :deep(ul) {
	list-style-type: disc;
}

.ai-text :deep(ol) {
	list-style-type: decimal;
}

.ai-text :deep(li) {
	margin: 4px 0;
	line-height: 1.7;
}

.ai-text :deep(li > ul),
.ai-text :deep(li > ol) {
	margin: 4px 0;
}

/* ===== 引用 ===== */
.ai-text :deep(blockquote) {
	margin: 12px 0;
	padding: 12px 16px;
	border-left: 4px solid #48dbfb;
	background: #f7f9fc;
	color: #555;
	border-radius: 0 6px 6px 0;
}

.ai-text :deep(blockquote p) {
	margin: 4px 0;
}

/* ===== 链接 ===== */
.ai-text :deep(a) {
	color: #48dbfb;
	text-decoration: none;
	border-bottom: 1px solid transparent;
	transition: border-color 0.2s;
}

.ai-text :deep(a:hover) {
	border-bottom-color: #48dbfb;
}

/* ===== 分隔线 ===== */
.ai-text :deep(hr) {
	border: none;
	border-top: 1px solid #e8e8e8;
	margin: 16px 0;
}

/* ===== 删除线 ===== */
.ai-text :deep(del),
.ai-text :deep(s) {
	text-decoration: line-through;
	color: #999;
}

.ai-text :deep(strong) {
	font-weight: 700;
	color: #1a1a2e;
}

.ai-text :deep(em) {
	font-style: italic;
}

/* ===== 图片 ===== */
.ai-text :deep(img) {
	max-width: 100%;
	border-radius: 6px;
	margin: 8px 0;
}

/* ===== 任务列表 ===== */
.ai-text :deep(input[type="checkbox"]) {
	margin-right: 6px;
	vertical-align: middle;
}

.message-time {
	font-size: 11px;
	color: #bbb;
	margin-top: 4px;
}

.typing-indicator {
	display: flex;
	align-items: center;
	padding: 12px 16px;
	gap: 5px;
}

.typing-indicator span {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #48dbfb;
	animation: typing 1.4s infinite both;
}

.typing-indicator span:nth-child(2) {
	animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
	animation-delay: 0.4s;
}

@keyframes typing {
	0%, 80%, 100% {
		transform: scale(0.6);
		opacity: 0.4;
	}
	40% {
		transform: scale(1);
		opacity: 1;
	}
}

@media (max-width: 768px) {
	.message-content {
		max-width: 85%;
	}
	.chat-messages {
		padding: 12px 16px;
	}
}
</style>

<style>
/* ===== 动态内容的非 scoped 样式 ===== */

/* ===== 表格 ===== */
.ai-text table.ui.celled.table {
	margin: 12px 0;
	font-size: 14px;
	border-collapse: separate;
	border-spacing: 0;
	border-radius: 6px;
	overflow: hidden;
	border: 1px solid #dfe3e8;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.ai-text table.ui.celled.table thead th {
	background: #f4f6f9;
	color: #1a1a2e;
	font-weight: 600;
	font-size: 13px;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	padding: 10px 14px;
	border-bottom: 2px solid #dfe3e8;
	border-right: 1px solid #e8ecf1;
	text-align: left;
	white-space: nowrap;
}

.ai-text table.ui.celled.table thead th:last-child {
	border-right: none;
}

.ai-text table.ui.celled.table tbody td {
	padding: 9px 14px;
	border-bottom: 1px solid #eef0f4;
	border-right: 1px solid #eef0f4;
	color: #3c4858;
	vertical-align: top;
	word-break: break-word;
}

.ai-text table.ui.celled.table tbody td:last-child {
	border-right: none;
}

.ai-text table.ui.celled.table tbody tr:last-child td {
	border-bottom: none;
}

.ai-text table.ui.celled.table tbody tr:nth-child(even) td {
	background: #fafbfc;
}

.ai-text table.ui.celled.table tbody tr:hover td {
	background: #f0f5ff;
}

/* ===== 打字机光标 ===== */
.ai-text .typing-cursor {
	display: inline-block;
	width: 2.5px;
	height: 1.15em;
	background: linear-gradient(180deg, #48dbfb, #6c5ce7);
	margin-left: 2px;
	vertical-align: text-bottom;
	border-radius: 1px;
	animation: cursor-blink 1s step-end infinite;
	box-shadow: 0 0 4px rgba(72, 219, 251, 0.4);
}

@keyframes cursor-blink {
	0%, 100% { opacity: 1; }
	50% { opacity: 0; }
}
</style>
